import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tab, TabItem } from '@blaze-react/tab';
import Select from '@blaze-react/select';
import Switches from '@blaze-react/switches';
import { useToasts } from '@blaze-react/toaster';
import { useEcommerceContext } from '@blaze-cms/ecommerce-context';
import {
  isSubscriptionProduct,
  isSingleIssueProduct,
  getSubscriptionTypeGroups,
  getOptionGroups,
  getVariantForSelections,
  hasAttributes,
  getRegionOptions,
  getVariantsForRegion,
  getOfferClassToken
} from './helpers';
import { EVENT_NAMES, FUTURE_START_DATE_KEY } from '../../constants';
import {
  PRODUCT_VARIANTS_CLASSNAME,
  PRODUCT_VARIANTS_ANCHOR_ID,
  AFTER_CHRISTMAS_TOGGLE_LABEL
} from './constants';

// Variant names from Shopware CMS contain HTML markup (e.g. <b>, <br />).
// This is CMS-authored content, not user input, so dangerouslySetInnerHTML is intentional.
const VariantName = ({ name, isDirectDebit }) => (
  // eslint-disable-next-line react/no-danger
  <div
    data-is-direct-debit={isDirectDebit}
    className={`${PRODUCT_VARIANTS_CLASSNAME}__variant__name${isDirectDebit ? ` ${PRODUCT_VARIANTS_CLASSNAME}__variant__name--direct-debit` : ''}`}
    dangerouslySetInnerHTML={{ __html: name }}
  />
);

VariantName.propTypes = { name: PropTypes.string, isDirectDebit: PropTypes.bool };
VariantName.defaultProps = { name: '', isDirectDebit: false };

const AddToBasketButton = ({ variant, itemId, isCartLoading, onAddToCart, emitCartEvent }) => {
  const { variantId, isAvailable } = variant;

  useEffect(() => {
    if (!emitCartEvent) return;

    emitCartEvent({
      eventName: EVENT_NAMES.view_item,
      input: { selectedVariant: variant, parentId: itemId }
    });
    // emitCartEvent is recreated on every render (not memoized upstream); only re-fire on variant/item change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId, itemId]);

  return (
    <button
      type="button"
      className={`${PRODUCT_VARIANTS_CLASSNAME}__add-to-cart button`}
      disabled={!isAvailable || isCartLoading}
      onClick={() => onAddToCart(variantId)}>
      Add to basket
    </button>
  );
};

AddToBasketButton.propTypes = {
  variant: PropTypes.shape({
    variantId: PropTypes.string.isRequired,
    isAvailable: PropTypes.bool
  }).isRequired,
  itemId: PropTypes.string.isRequired,
  isCartLoading: PropTypes.bool,
  onAddToCart: PropTypes.func.isRequired,
  emitCartEvent: PropTypes.func
};
AddToBasketButton.defaultProps = { isCartLoading: false, emitCartEvent: null };

const SubscriptionTabContent = ({
  typeVariants,
  isCartLoading,
  handleAddToCart,
  className,
  selectedRegion,
  onRegionChange,
  itemId,
  emitCartEvent,
  showAfterChristmasToggle,
  startAfterChristmas,
  onStartAfterChristmasChange,
  toggleId
}) => {
  const regionOptions = getRegionOptions(typeVariants);
  // Use the shared region if it exists in this tab's options, otherwise fall back to the first available
  const effectiveRegion =
    regionOptions.find(opt => opt.valueName === selectedRegion)?.valueName ||
    regionOptions[0]?.valueName ||
    '';

  const visibleVariants = effectiveRegion
    ? getVariantsForRegion(typeVariants, effectiveRegion)
    : typeVariants;

  const sortedVisibleVariants = [...visibleVariants].sort((leftVariant, rightVariant) => {
    const leftSavings = leftVariant.attributes?.savingsPercentage;
    const rightSavings = rightVariant.attributes?.savingsPercentage;
    const leftPricePerIssue = leftVariant.attributes?.pricePerIssue;
    const rightPricePerIssue = rightVariant.attributes?.pricePerIssue;

    if (leftSavings == null && rightSavings == null) {
      if (leftPricePerIssue == null && rightPricePerIssue == null) return 0;
      if (leftPricePerIssue == null) return 1;
      if (rightPricePerIssue == null) return -1;

      return leftPricePerIssue - rightPricePerIssue;
    }

    if (leftSavings == null) return 1;
    if (rightSavings == null) return -1;

    if (leftSavings === rightSavings) {
      if (leftPricePerIssue == null && rightPricePerIssue == null) return 0;
      if (leftPricePerIssue == null) return 1;
      if (rightPricePerIssue == null) return -1;

      return leftPricePerIssue - rightPricePerIssue;
    }

    return rightSavings - leftSavings;
  });

  return (
    <div className={`${className}__tab-content`}>
      {(regionOptions.length > 0 || showAfterChristmasToggle) && (
        <div className={`${className}__controls`}>
          {regionOptions.length > 0 && (
            <Select
              label="Region"
              options={regionOptions.map(({ valueName }) => valueName)}
              selected={effectiveRegion}
              showDefaultOption={false}
              onChange={({ value }) => onRegionChange(value)}
            />
          )}
          {showAfterChristmasToggle && (
            <div className={`${className}__after-christmas-toggle`}>
              <Switches
                options={{
                  id: toggleId,
                  label: AFTER_CHRISTMAS_TOGGLE_LABEL,
                  checked: startAfterChristmas
                }}
                labelPosition="left"
                returnBoolean
                onChange={({ value }) => onStartAfterChristmasChange(value)}
              />
            </div>
          )}
        </div>
      )}
      {sortedVisibleVariants.map((variant, i) => {
        const { price } = variant;
        return (
          <div
            key={variant.variantId}
            className={`${className}__variant ${i === 0 ? `${className}__variant--first` : ''}`}>
            <div className={`${className}__offer-row`}>
              <VariantName
                name={variant.name}
                isDirectDebit={
                  variant.attributes?.customFields?.product_subscription_details_is_direct_debit
                }
              />
              {price && (
                <div className={`${className}__variant__price`}>
                  {variant.price}
                  {variant.attributes?.savingsPercentage != null && (
                    <>
                      <br />
                      {variant.attributes?.savingsPercentage}% saving
                    </>
                  )}
                </div>
              )}
            </div>
            <div className={`${className}__offer-row`}>
              <div className={`${className}__variant__item-price`}>
                {variant.attributes.formattedPricePerIssue
                  ? `${variant.attributes.formattedPricePerIssue}${!variant.attributes?.is_digital ? ' an issue (incl. postage)' : ''} `
                  : ''}
              </div>
              <AddToBasketButton
                variant={variant}
                itemId={itemId}
                isCartLoading={isCartLoading}
                onAddToCart={handleAddToCart}
                emitCartEvent={emitCartEvent}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

SubscriptionTabContent.propTypes = {
  typeVariants: PropTypes.array.isRequired,
  isCartLoading: PropTypes.bool.isRequired,
  handleAddToCart: PropTypes.func.isRequired,
  className: PropTypes.string.isRequired,
  selectedRegion: PropTypes.string.isRequired,
  onRegionChange: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  emitCartEvent: PropTypes.func,
  showAfterChristmasToggle: PropTypes.bool,
  startAfterChristmas: PropTypes.bool,
  onStartAfterChristmasChange: PropTypes.func,
  toggleId: PropTypes.string
};
SubscriptionTabContent.defaultProps = {
  emitCartEvent: null,
  showAfterChristmasToggle: false,
  startAfterChristmas: false,
  onStartAfterChristmasChange: () => {},
  toggleId: undefined
};

const SingleIssueCTA = ({ product, formattedPrice }) => (
  <div className="row row--wide product--single-issue--cta display-row">
    <div className="column column--three-quarters">
      <p>
        <strong>Single issue</strong>
        <br />
        One issue
      </p>
      <div className="stats">
        <div className="stats__heading">
          <span>{formattedPrice} an issue</span>
        </div>
      </div>
    </div>
    <div className="column column--one-quarter">
      <div className="stats stats--align--right">
        <div className="stats__heading">
          <span className="align--right">{formattedPrice}</span>
        </div>
      </div>
      <a
        aria-label="View all single issues"
        className="button button--cta button--right button--outline"
        href={`/single-issue${product.url}`}>
        View all single issues
      </a>
    </div>
  </div>
);

SingleIssueCTA.propTypes = {
  product: PropTypes.shape({
    url: PropTypes.string.isRequired
  }).isRequired,
  formattedPrice: PropTypes.string
};
SingleIssueCTA.defaultProps = { formattedPrice: null };

const SubscriptionCTA = ({ subscriptionCta }) => (
  <div className="card card--subscription-cta card--landscape">
    <div className="card__content-wrapper card__content-wrapper--subscription-cta card__content-wrapper--landscape">
      <div className="card__content card__content--subscription-cta card__content--landscape">
        <p>Purchase a subscription</p>
        <div className="card__details card__details--subscription-cta card__details--landscape">
          <span className="price">From {subscriptionCta.fromPrice}</span>
        </div>
      </div>
      <div className="card__child-content">
        <a
          aria-label="View Offers"
          className="button button--dark button--left button--small"
          href={subscriptionCta.url}>
          View Offers
        </a>
      </div>
    </div>
  </div>
);

SubscriptionCTA.propTypes = {
  subscriptionCta: PropTypes.shape({
    url: PropTypes.string.isRequired,
    fromPrice: PropTypes.string.isRequired
  }).isRequired
};

const ProductVariants = ({
  product,
  variants,
  itemId,
  currency,
  hasSingleIssues,
  subscriptionCta,
  formattedPricePerIssue,
  offer,
  enableAfterChristmasToggle,
  afterChristmasStartDate
}) => {
  const {
    handleCreateCart,
    handleAddItemsToCart,
    cartId,
    isCartLoading,
    setContextState,
    emitCartEvent
  } = useEcommerceContext();
  const { addToast = () => {} } = useToasts();
  const [selections, setSelections] = useState({});
  const [selectedRegion, setSelectedRegion] = useState('');
  // The switch starts on when the url offer matched variants (Christmas offers are shown) and
  // off when base prices are shown. `offer` is only passed when it matched. The choice is kept
  // per product and offer, so a client side navigation that reuses this component starts from
  // the new page's default rather than the previous page's choice.
  const afterChristmasKey = `${itemId}|${offer || ''}`;
  const [afterChristmasChoice, setAfterChristmasChoice] = useState({
    key: afterChristmasKey,
    on: !!offer
  });
  const startAfterChristmas =
    afterChristmasChoice.key === afterChristmasKey ? afterChristmasChoice.on : !!offer;
  const setStartAfterChristmas = on => setAfterChristmasChoice({ key: afterChristmasKey, on });
  // The switch only shows when Darwin gave the product a start date to send
  const showAfterChristmasToggle = enableAfterChristmasToggle && !!afterChristmasStartDate;

  if (!variants?.length) return null;

  const onError = error => {
    addToast(`Cart error: ${error.message}`, { appearance: 'error', autoDismiss: true });
  };

  const onCompleteCartAction = () => {
    addToast('Added to basket', { appearance: 'success', autoDismiss: true });
  };

  const handleAddToCart = (variantId, additionalData) => {
    if (isCartLoading) return;

    const handler = cartId ? handleAddItemsToCart : handleCreateCart;
    const lineItem = { productId: itemId, variantId, quantity: 1 };
    if (additionalData) lineItem.additionalData = additionalData;
    handler({
      lineItems: [lineItem],
      onCompleted: () => onCompleteCartAction(variantId),
      onError
    });
  };

  const className = PRODUCT_VARIANTS_CLASSNAME;
  const offerClassToken = getOfferClassToken(offer);
  const getWrapperClassName = mode =>
    [
      className,
      `${className}--${mode}`,
      offerClassToken && `${className}--offer-${offerClassToken}`
    ]
      .filter(Boolean)
      .join(' ');

  // ── Subscription mode ────────────────────────────────────────────────────────
  if (isSubscriptionProduct(product)) {
    const subscriptionGroups = getSubscriptionTypeGroups(variants);
    const groupEntries = Object.entries(subscriptionGroups);

    // Fall through to other modes if no subscription type options are present at all.
    // A single subscription type still renders as a single tab, rather than falling
    // through to the multi-attribute select mode.
    if (groupEntries.length >= 1) {
      const handleSubscriptionAddToCart = variantId =>
        handleAddToCart(
          variantId,
          showAfterChristmasToggle && startAfterChristmas
            ? { [FUTURE_START_DATE_KEY]: afterChristmasStartDate }
            : undefined
        );

      return (
        <div id={PRODUCT_VARIANTS_ANCHOR_ID} className={getWrapperClassName('subscription')}>
          <Tab selected={0}>
            {groupEntries.map(([subscriptionType, typeVariants], index) => (
              <TabItem key={subscriptionType} title={subscriptionType}>
                <SubscriptionTabContent
                  // Switches keeps its own checked state, so remount for a new product or offer
                  key={afterChristmasKey}
                  typeVariants={typeVariants}
                  isCartLoading={isCartLoading}
                  handleAddToCart={handleSubscriptionAddToCart}
                  className={className}
                  selectedRegion={selectedRegion}
                  onRegionChange={setSelectedRegion}
                  itemId={itemId}
                  emitCartEvent={emitCartEvent}
                  showAfterChristmasToggle={showAfterChristmasToggle}
                  startAfterChristmas={startAfterChristmas}
                  onStartAfterChristmasChange={setStartAfterChristmas}
                  toggleId={`${className}-after-christmas-${index}`}
                />
              </TabItem>
            ))}
          </Tab>
          {hasSingleIssues && formattedPricePerIssue && (
            <SingleIssueCTA product={product} formattedPrice={formattedPricePerIssue} />
          )}
        </div>
      );
    }
  }

  // ── Single issue mode ─────────────────────────────────────────────────────────
  if (isSingleIssueProduct(product)) {
    return (
      <div id={PRODUCT_VARIANTS_ANCHOR_ID} className={getWrapperClassName('single-issue')}>
        {variants.map(variant => {
          const { price } = variant;
          return (
            <div key={variant.variantId} className={`${className}__variant`}>
              <div className={`${className}__offer-row`}>
                <VariantName
                  name={`<b>${variant?.attributes?.selectedOptions[0]?.valueName}</b><br />One Issue`}
                />
                {price && <div className={`${className}__variant__price`}>{price}</div>}
              </div>
              <div className={`${className}__offer-row`}>
                <div className={`${className}__variant__item-price`} />
                <AddToBasketButton
                  variant={variant}
                  itemId={itemId}
                  isCartLoading={isCartLoading}
                  onAddToCart={handleAddToCart}
                  emitCartEvent={emitCartEvent}
                />
              </div>
            </div>
          );
        })}
        {subscriptionCta && <SubscriptionCTA subscriptionCta={subscriptionCta} />}
      </div>
    );
  }

  // ── Multi-attribute mode (colour, size, etc.) ─────────────────────────────────
  if (hasAttributes(variants)) {
    const optionGroups = getOptionGroups(variants);
    const initialSelections = Object.entries(optionGroups).reduce(
      (acc, [label, { values }]) => ({ ...acc, [label]: values[0]?.valueName }),
      {}
    );
    const currentSelections = Object.keys(selections).length ? selections : initialSelections;
    const selectedVariant = getVariantForSelections(variants, currentSelections);

    const handleSelectChange = (optionLabel, valueName) => {
      const newSelections = { ...currentSelections, [optionLabel]: valueName };
      setSelections(newSelections);
      const variant = getVariantForSelections(variants, newSelections);
      if (variant) setContextState('variant', variant.variantId);
    };

    return (
      <div id={PRODUCT_VARIANTS_ANCHOR_ID} className={getWrapperClassName('select')}>
        {Object.entries(optionGroups).map(([optionLabel, { values }]) => (
          <Select
            key={optionLabel}
            label={optionLabel}
            options={values.map(({ valueName }) => valueName)}
            selected={currentSelections[optionLabel] || ''}
            showDefaultOption={false}
            onChange={({ value }) => handleSelectChange(optionLabel, value)}
          />
        ))}
        {selectedVariant && (
          <div key={selectedVariant.variantId} className={`${className}__variant`}>
            <div className={`${className}__offer-row`}>
              <VariantName name="<b>Product</b>" />
              {selectedVariant.price && (
                <div className={`${className}__variant__price`}>{selectedVariant.price}</div>
              )}
            </div>
            <div className={`${className}__offer-row`}>
              <div className={`${className}__variant__item-price`} />
              <AddToBasketButton
                variant={selectedVariant}
                itemId={itemId}
                isCartLoading={isCartLoading}
                onAddToCart={handleAddToCart}
                emitCartEvent={emitCartEvent}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Simple mode — no variant attributes ──────────────────────────────────────
  return (
    <div id={PRODUCT_VARIANTS_ANCHOR_ID} className={getWrapperClassName('simple')}>
      {variants.map(variant => (
        <div key={variant.variantId} className={`${className}__variant`}>
          <div className={`${className}__offer-row`}>
            <VariantName name="<b>Product</b>" />
            {variant.price && <div className={`${className}__variant__price`}>{variant.price}</div>}
          </div>
          <div className={`${className}__offer-row`}>
            <div className={`${className}__variant__item-price`} />
            <AddToBasketButton
              variant={variant}
              itemId={itemId}
              isCartLoading={isCartLoading}
              onAddToCart={handleAddToCart}
              emitCartEvent={emitCartEvent}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

ProductVariants.propTypes = {
  product: PropTypes.object,
  hasSingleIssues: PropTypes.bool,
  subscriptionCta: PropTypes.shape({
    url: PropTypes.string,
    fromPrice: PropTypes.string
  }),
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      variantId: PropTypes.string.isRequired,
      name: PropTypes.string,
      isAvailable: PropTypes.bool,
      prices: PropTypes.object,
      attributes: PropTypes.shape({
        selectedOptions: PropTypes.arrayOf(
          PropTypes.shape({
            optionLabel: PropTypes.string,
            valueName: PropTypes.string,
            valueId: PropTypes.string,
            optionId: PropTypes.string
          })
        ),
        customFields: PropTypes.object
      })
    })
  ),
  itemId: PropTypes.string.isRequired,
  currency: PropTypes.string,
  formattedPricePerIssue: PropTypes.string,
  offer: PropTypes.string,
  enableAfterChristmasToggle: PropTypes.bool,
  afterChristmasStartDate: PropTypes.string
};

ProductVariants.defaultProps = {
  product: null,
  hasSingleIssues: false,
  subscriptionCta: null,
  variants: [],
  currency: 'gbp',
  formattedPricePerIssue: null,
  offer: null,
  enableAfterChristmasToggle: false,
  afterChristmasStartDate: null
};

export default ProductVariants;
